'use client';

import Link from 'next/link';
import Image from 'next/image';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-white pt-16 pb-8 border-t border-gray-100" itemScope itemType="https://schema.org/WPFooter">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-x-8 gap-y-12">
          {/* Logo et description */}
          <div className="col-span-1 md:col-span-1">
            <Link href="/" className="inline-block mb-4 transition-opacity hover:opacity-90" aria-label="Page d'accueil">
              <Image 
                src="/images/Logo-MCR_Avocate.png" 
                alt="Maître Marie-Camille Roch" 
                width={150} 
                height={60} 
                className="h-auto"
              />
            </Link>
            <p className="text-sm text-gray-800 max-w-xs mt-2">
              Avocate au Barreau de Montpellier.
            </p>
            
            {/* Social Media */}
            <div className="flex space-x-4 mt-6">
              <a 
                href="https://www.linkedin.com/in/marie-camille-roch-70b078202/"
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-primary text-white p-2 rounded-full hover:bg-primary/90 transition-colors duration-300"
                aria-label="LinkedIn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a 
                href="mailto:contact@mcroch-avocat.com" 
                className="bg-primary text-white p-2 rounded-full hover:bg-primary/90 transition-colors duration-300"
                aria-label="Email"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M0 3v18h24v-18h-24zm21.518 2l-9.518 7.713-9.518-7.713h19.036zm-19.518 14v-11.817l10 8.104 10-8.104v11.817h-20z"/>
                </svg>
              </a>
              <a 
                href="tel:+33608357017" 
                className="bg-primary text-white p-2 rounded-full hover:bg-primary/90 transition-colors duration-300"
                aria-label="Téléphone"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20 22.621l-3.521-6.795c-.008.004-1.974.97-2.064 1.011-2.24 1.086-6.799-7.82-4.609-8.994l2.083-1.026-3.493-6.817-2.106 1.039c-7.202 3.755 4.233 25.982 11.6 22.615.121-.055 2.102-1.029 2.11-1.033z"/>
                </svg>
              </a>
            </div>
          </div>
          
          {/* Coordonnées */}
          <div className="col-span-1 md:col-span-1">
            <h3 className="text-lg font-bold mb-6 text-gray-800 relative inline-block">
              Coordonnées
              <span className="absolute -bottom-1 left-0 h-0.5 w-12 bg-primary"></span>
            </h3>
            <div className="space-y-4" itemScope itemType="https://schema.org/Attorney">
              <div className="flex items-start space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-primary mt-0.5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
                <div>
                  <p className="font-medium text-gray-800">Adresse :</p>
                  <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                    <p className="text-sm text-gray-700 mt-1" itemProp="streetAddress">24 Rue Durand</p>
                    <p className="text-sm text-gray-700">
                      <span itemProp="postalCode">34000</span> <span itemProp="addressLocality">Montpellier</span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-primary mt-0.5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                <div>
                  <p className="font-medium text-gray-800">Courriel :</p>
                  <a 
                    href="mailto:contact@mcroch-avocat.com" 
                    className="text-sm text-gray-700 hover:text-primary transition-colors duration-300 mt-1 inline-block"
                    itemProp="email"
                  >
                    contact@mcroch-avocat.com
                  </a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-primary mt-0.5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                </svg>
                <div>
                  <p className="font-medium text-gray-800">Téléphone :</p>
                  <a 
                    href="tel:+33608357017" 
                    className="text-sm text-gray-700 hover:text-primary transition-colors duration-300 mt-1 inline-block"
                    itemProp="telephone"
                  >
                    06.08.35.70.17
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Horaires */}
          <div className="col-span-1 md:col-span-1">
            <h3 className="text-lg font-bold mb-6 text-gray-800 relative inline-block">
              Horaires
              <span className="absolute -bottom-1 left-0 h-0.5 w-12 bg-primary"></span>
            </h3>
            <div className="space-y-4" itemScope itemType="https://schema.org/LocalBusiness">
              <meta itemProp="name" content="Cabinet Marie-Camille Roch" />
              <div className="flex items-start space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-primary mt-0.5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-medium text-gray-800">Jours ouvrables :</p>
                  <div itemProp="openingHoursSpecification" itemScope itemType="https://schema.org/OpeningHoursSpecification">
                    <meta itemProp="dayOfWeek" content="Monday Tuesday Wednesday Thursday Friday" />
                    <meta itemProp="opens" content="09:30" />
                    <meta itemProp="closes" content="20:00" />
                    <p className="text-sm text-gray-700 mt-1">Du lundi au vendredi</p>
                    <p className="text-sm text-gray-700">9h30 - 20h00</p>
                  </div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-primary mt-0.5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
                </svg>
                <div>
                  <p className="font-medium text-gray-800">Week-end :</p>
                  <p className="text-sm text-gray-700 mt-1">Samedi et dimanche fermé</p>
                  <p className="text-sm text-gray-700 italic">(possibilité de rendez-vous en urgence)</p>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <Link 
                href="/contact" 
                className="text-primary hover:text-primary/80 font-medium text-sm inline-flex items-center border-b border-primary/30 hover:border-primary transition-colors duration-300 pb-1"
                aria-label="Prendre rendez-vous avec Maître Marie-Camille Roch"
              >
                Prendre rendez-vous
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ml-1" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75" />
                </svg>
              </Link>
            </div>
          </div>
          
          {/* Liens rapides */}
          <div className="col-span-1 md:col-span-1">
            <h3 className="text-lg font-bold mb-6 text-gray-800 relative inline-block">
              Liens rapides
              <span className="absolute -bottom-1 left-0 h-0.5 w-12 bg-primary"></span>
            </h3>
            <nav aria-label="Navigation rapide">
              <ul className="space-y-2.5">
                <li>
                  <Link 
                    href="/a-propos" 
                    className="text-sm text-gray-700 hover:text-primary transition-colors flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2 text-primary" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                    À propos du cabinet
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/services/droit-penal" 
                    className="text-sm text-gray-700 hover:text-primary transition-colors flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2 text-primary" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                    Droit pénal
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/services/effacement-fichiers" 
                    className="text-sm text-gray-700 hover:text-primary transition-colors flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2 text-primary" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                    Effacement de fichiers
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/services/dommages-corporels" 
                    className="text-sm text-gray-700 hover:text-primary transition-colors flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2 text-primary" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                    Dommages corporels
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/honoraires" 
                    className="text-sm text-gray-700 hover:text-primary transition-colors flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2 text-primary" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                    Nos honoraires
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/contact" 
                    className="text-sm text-gray-700 hover:text-primary transition-colors flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2 text-primary" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                    Contact
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/blog/fichier-taj-gestion-effacement-donnees" 
                    className="text-sm text-gray-700 hover:text-primary transition-colors flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2 text-primary" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                    Fichier TAJ : Infos
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
        
        <div className="border-t border-gray-200 mt-12 pt-8 flex flex-col md:flex-row md:justify-between md:items-center text-sm text-gray-600">
          <p>© {currentYear} Cabinet de Maître Marie-Camille Roch. Tous droits réservés.</p>
          <div className="mt-4 md:mt-0 space-x-4 flex">
            <Link href="/mentions-legales" className="hover:text-primary transition-colors">Mentions légales</Link>
            <Link href="/confidentialite" className="hover:text-primary transition-colors">Politique de confidentialité</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
